import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { TargetRaceThemeConfig } from '../races.utils';
import { TargetRaceConfig } from 'src/replicant/match2/races/races.ruleset';
import { user } from 'src/StateObserver';
import { duration } from 'src/replicant/utils/duration';

export type RacesEntryParams = {
  name: string;
  position: number;
  score: number;
  previousScore: number;
  avatar: string;
  me?: boolean;
  alt?: boolean;
};

export class TargetRacesEntry extends View {
  private readonly positionIcon: ImageScaleView;
  private readonly name: LangBitmapFontTextView;
  private readonly left: View;
  private readonly right: View;
  private readonly racer: ImageScaleView;
  private readonly playerFrame: ImageScaleView;
  private readonly score: LangBitmapFontTextView;
  private readonly racerParent: View;

  constructor(
    opts: ViewOpts,
    private readonly config: TargetRaceConfig,
    private readonly theme: TargetRaceThemeConfig,
  ) {
    super({ width: 643, height: 116, ...opts });

    this.left = new View({
      superview: this,
    });

    this.right = new View({
      superview: this,
    });

    new ImageScaleView({
      superview: this.right,
      image: `assets/ui/match2/races/common/finish_line.png`,
      width: 58,
      height: 116,
      x: this.style.width - 28,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    new ImageScaleView({
      superview: this,
      width: 643,
      height: 10,
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: 0,
      ...theme.leaderboard.divider,
    });

    this.playerFrame = new ImageScaleView({
      superview: this.left,
      ...theme.leaderboard.playerFrame,
      centerOnOrigin: true,
      x: 83,
      y: this.style.height * 0.5,
    });

    this.name = new LangBitmapFontTextView({
      superview: this.playerFrame,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 150,
      height: this.playerFrame.style.height,
      color: '#754111',
      x: 30,
      y: -3,
    });

    this.positionIcon = new ImageScaleView({
      superview: this.right,
      x: this.style.width - 51,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 62,
      height: 62,
    });

    this.racerParent = new View({
      superview: this,
      width: 112,
      height: 76,
      centerOnOrigin: true,
      y: this.style.height * 0.5,
    });

    const scoreFrame = new ImageScaleView({
      superview: this.racerParent,
      ...theme.leaderboard.scoreFrame,
      x: -15,
      y: -13,
    });

    this.score = new LangBitmapFontTextView({
      superview: scoreFrame,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Title',
      width: scoreFrame.style.width,
      height: scoreFrame.style.height,
      x: -3,
      y: -6,
    });
    this.racer = new ImageScaleView({
      superview: this.racerParent,
      x: this.racerParent.style.width * 0.5,
      y: this.racerParent.style.height * 0.5,
    });
  }

  public setup(params: RacesEntryParams) {
    this.name.localeText = () => params.name;

    if (params.position >= 1 && params.position <= 3) {
      this.positionIcon.updateOpts({
        image: `assets/ui/match2/tournament/star_tournament_medal_${params.position}.png`,
        visible: true,
      });
    } else {
      this.positionIcon.updateOpts({ visible: false });
    }

    this.setScore(params.previousScore);

    const delta = params.score - params.previousScore;
    if (delta > 0) {
      const animDuration = Math.max(delta * 300, 600);
      animate({ score: params.previousScore })
        .wait(300)
        .then(
          (value) => {
            this.racerParent.style.r =
              Math.cos((delta / 4) * value * Math.PI * 2) * 0.1;
            this.setScore(params.previousScore + value * delta);
          },
          animDuration,
          animate.easeInOutQuad,
        )
        .then(() => {
          this.racerParent.style.r = 0;
        });
    }

    this.racer.updateOpts({
      ...(params.me
        ? this.theme.leaderboard.racerMe
        : this.theme.leaderboard.racer),
      centerOnOrigin: true,
    });

    this.playerFrame.updateOpts({
      ...(params.me
        ? this.theme.leaderboard.playerFrameMe
        : this.theme.leaderboard.playerFrame),
      centerOnOrigin: true,
    });
    this.name.updateOpts({
      color: params.me ? '#ffffff' : '#754111',
    });
  }

  private setScore(score: number) {
    const racerStart = 250;
    const maxRacerPosition =
      this.style.width - this.racer.style.width * 0.5 - racerStart - 30;
    const progress = Math.min(score / this.config.target, 1);

    this.racerParent.updateOpts({
      x: progress * maxRacerPosition + racerStart,
    });

    this.score.localeText = () => Math.round(score).toString();
  }
}
